import * as analytics from '../analytics';
import { connect } from 'react-redux';
import { RouteComponentProps, Redirect } from 'react-router-dom';
import { ApplicationState } from '../store';
import { UserState } from '../store/User';
import { CommonOperations, contains } from '../store/permissions';
import { TenantState, Subscription, PPMFeatures, StatusCalculationTypes } from '../store/Tenant';
import ProjectStatusCalculationSettingsTooltip from "./common/sectionsControl/uiControls/projectStatusCalculationSettingsControl/ProjectStatusCalculationSettingsTooltip";
import ProgramStatusCalculationSettingsTooltip from "./common/sectionsControl/uiControls/programsStatusCalculationSettingsControl/ProgramStatusCalculationSettingsTooltip";
import PortfolioStatusCalculationSettingsTooltip from "./common/sectionsControl/uiControls/portfoliosStatusCalculationSettingsControl/PortfolioStatusCalculationSettingsTooltip"
import SectionableSettingsPage from './settings/SectionableSettingsPage';
import { Section } from '../entities/Metadata';

export const InsightsSettingsSectionKeys = {
    keyDatesStatuses: "keyDatesStatuses",
    tasksStatuses: "tasksStatuses",
    objectivesAndKeyResultsStatuses: "objectivesAndKeyResultsStatuses",
    warnings: "warnings",
    projectsStatuses: "projectsStatuses",
    programsStatuses: "programsStatuses",
    portfoliosStatuses: "portfoliosStatuses"
} as const;

type InsightsSettingsSectionKeys = typeof InsightsSettingsSectionKeys[keyof typeof InsightsSettingsSectionKeys];

const insightsSettingsSectionMap: Record<InsightsSettingsSectionKeys,
    { name: string, iconName: string, tooltip: JSX.Element | undefined, controlName: string, navigationLabel?: string }> = {
    [InsightsSettingsSectionKeys.keyDatesStatuses]: {
        name: 'Key Dates Statuses',
        iconName: 'PPMXSectionKeyDates',
        tooltip: undefined,
        controlName: 'KeyDatesStatusesSettingsControl',
    },
    [InsightsSettingsSectionKeys.tasksStatuses]: {
        name: 'Tasks Statuses',
        iconName: 'GroupedList',
        tooltip: undefined,
        controlName: 'TasksStatusesSettingsControl',
    },
    [InsightsSettingsSectionKeys.objectivesAndKeyResultsStatuses]: {
        name: 'Objectives and Key Results Statuses',
        navigationLabel: 'OKR Statuses',
        iconName: 'PPMXCompanyObjectives',
        tooltip: undefined,
        controlName: 'OKRStatusCalculationSettingsControl',
    },
    [InsightsSettingsSectionKeys.warnings]: {
        name: 'Warnings',
        iconName: 'Warning',
        tooltip: undefined,
        controlName: 'WarningsSettingsControl',
    },
    [InsightsSettingsSectionKeys.projectsStatuses]: {
        name: 'Projects Statuses',
        iconName: 'PPMXProject',
        tooltip: <ProjectStatusCalculationSettingsTooltip />,
        controlName: 'ProjectStatusCalculationSettingsControl',
    },
    [InsightsSettingsSectionKeys.programsStatuses]: {
        name: 'Programs Statuses',
        iconName: 'PPMXProgram',
        tooltip: <ProgramStatusCalculationSettingsTooltip />,
        controlName: 'ProgramStatusCalculationSettingsControl',
    },
    [InsightsSettingsSectionKeys.portfoliosStatuses]: {
        name: 'Portfolios Statuses',
        iconName: 'PPMXPortfolio',
        tooltip: <PortfolioStatusCalculationSettingsTooltip />,
        controlName: 'PortfolioStatusCalculationSettingsControl',
    },
};

const buildSectionSettings = (key: InsightsSettingsSectionKeys, isSelected = true, isOpen = true, isAccessible = true, uiControlsSections = []): Section => ({
    id: key,
    name: insightsSettingsSectionMap[key].name,
    navigationLabel: insightsSettingsSectionMap[key].navigationLabel,
    settings: {
        iconName: insightsSettingsSectionMap[key].iconName,
        tooltip: insightsSettingsSectionMap[key].tooltip,
    },
    isSelected: isSelected,
    isOpen: isOpen,
    isAccessible: isAccessible,
    uiControls: [{
        id: key,
        type: insightsSettingsSectionMap[key].controlName,
        settings: uiControlsSections
    }]
});


type OwnProps = RouteComponentProps<{}>;
type StateProps = {
    user: UserState;
    tenant: TenantState;
}
type Props = OwnProps & StateProps;

export const trackStatusCalculation = (name: string, user: analytics.CreatedBy, type: StatusCalculationTypes) => {
    analytics.trackEvent(name, user, { value: StatusCalculationTypes[type] });
}

const InsightsSettings = (props: Props) => {
    const { tenant, user } = props;

    if (!tenant.id
        || !user.isSignedUp
        || !contains(user.permissions.common, CommonOperations.AdministrateView)) {
        return <Redirect to="/" />;
    }

    const isPortfolioOrProjectManagmentAvailable = Subscription.containsAny(tenant.subscription, [PPMFeatures.PortfolioManagement, PPMFeatures.ProjectManagement]);
    const isProjectManagmentAvailable = Subscription.contains(tenant.subscription, PPMFeatures.ProjectManagement);
    const isOKRAvailable = Subscription.contains(tenant.subscription, PPMFeatures.OKR);
    const isPortfolioManagmentAvailable = Subscription.contains(tenant.subscription, PPMFeatures.PortfolioManagement);

    const sections = [
        buildSectionSettings(InsightsSettingsSectionKeys.keyDatesStatuses, isPortfolioOrProjectManagmentAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.tasksStatuses, isProjectManagmentAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.objectivesAndKeyResultsStatuses, isOKRAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.warnings, isPortfolioOrProjectManagmentAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.projectsStatuses, isProjectManagmentAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.programsStatuses, isPortfolioManagmentAvailable),
        buildSectionSettings(InsightsSettingsSectionKeys.portfoliosStatuses, isPortfolioManagmentAvailable),
    ];

    return <SectionableSettingsPage
        className="insights-settings"
        title="PPM Insights Settings"
        isLoading={tenant.isProcessing}
        sections={sections}
        settingsKey='insightsSettings'
    />
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        user: state.user,
        tenant: state.tenant
    };
}

export default connect(mapStateToProps)(InsightsSettings);